import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PropTypes from 'prop-types';
import SideNavbar from "../components/SideNavbar";
import Error from "./Error";
import Loading from "./Loading";
import FormInput from "../components/FormInput";
import { convertUrlToFile, createDeepClone, getFilePseudoUUID, getImageNameFromPresignedURL, getObjectValue } from "../utils/utils";
import { RiFileEditLine, RiFileEditFill } from "react-icons/ri";
import HoverButton from "../components/HoverButton";
import DropZone from "../components/DropZone";

const defaultImageStatsObject = {
    'location': '',
    'acres': '',
    'numberOfTrailCameras': '',
    'surveyLength': '',
    'doeDetections': '',
    'fawnDetections': '',
    'deerDensity': '',
    'doeBuckRatio': '',
    'fawnDoeRatio': '',
    'doeEstimate': '',
    'fawnEstimate': '',
    'map': null
};

const defaultImageStatsErrorObject = {
    'locationError': '',
    'acresError': '',
    'numberOfTrailCamerasError': '',
    'surveyLengthError': '',
    'doeDetectionsError': '',
    'fawnDetectionsError': '',
    'deerDensityError': '',
    'doeBuckRatioError': '',
    'fawnDoeRatioError': '',
    'doeEstimateError': '',
    'fawnEstimateError': '',
    'mapError': ''
};

const imageStatsFormObject = {
    'location' : {
        'label': 'Location (City, State)',
        'type': '',
    },
    'acres': {
        'label': 'Number of Acres',
        'type': 'number',
    },
    'numberOfTrailCameras': {
        'label': 'Number of Trailcameras',
        'type': 'number'
    },
    'surveyLength': {
        'label': 'Survey Length',
        'type': '',
    },
    'doeDetections': {
        'label': 'Doe Detections',
        'type': 'number'
    },
    'fawnDetections': {
        'label': 'Fawn Detections',
        'type': 'number'
    },
    'deerDensity': {
        'label': 'Deer Density',
        'type': 'number',
        'step': '0.01'
    },
    'doeBuckRatio': {
        'label': 'Doe:Buck Ratio',
        'type': 'number',
        'step': '0.01'
    },
    'fawnDoeRatio': {
        'label': 'Fawn:Doe Ratio',
        'type': 'number',
        'step': '0.01'
    },
    'doeEstimate': {
        'label': 'Doe Estimate',
        'type': 'number'
    },
    'fawnEstimate': {
        'label': 'Fawn Estimate',
        'type': 'number'
    }
};

function SiteStats({ currentProfile, isMobile }) {
    const [selectedSite, setSelectedSite] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [updateError, setUpdateError] = useState('');
    const [updateSuccess, setUpdateSuccess] = useState('');
    const [formData, setFormData] = useState(createDeepClone(defaultImageStatsObject));
    const [formDataErrors, setFormDataErrors] = useState(createDeepClone(defaultImageStatsErrorObject));
    const [editStats, setEditStats] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const [existingImage, setExistingImage] = useState('');

    const { site } = useParams();

    let formattedSite = "site";
    if(site) {
        formattedSite = site.replaceAll("-wildlifeinvestments", "");
        let sites = formattedSite.split("-");
        formattedSite = sites.map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
    }

    useEffect(() => {
        document.title = `${formattedSite} Statistics`;
    });

    useEffect(() => {
        if(currentProfile && currentProfile.sites) {
            currentProfile.sites.forEach((s) => {
                if(s.slug === site) {
                    setSelectedSite(s);
                }
            });
        }
    }, [currentProfile, site]);

    const setFormDataFromSiteStats = async () => {
        let updatedFormData = {};
        await Promise.all(Object.keys(formData).map(async(key) => {
            let value = selectedSite[key];
            if(value === null) {
                value = '';
            } else if (value && key === "map") {
                let existingImageObject = { "url": value }
                Object.assign(existingImageObject, { "name": getImageNameFromPresignedURL(value) });
                value = await convertUrlToFile(value, "existing_map.JPG");
                Object.assign(existingImageObject, { "size": value.size, "lastModified": value.lastModified })
                setExistingImage(existingImageObject);
                value = { 'error': false, 'success': true, "name": "existing_map.JPG", "file": value };
            }
            Object.assign(updatedFormData, { [key]: value });
            return true;
        }));
        setFormData(updatedFormData);
    }

    useEffect(() => {
        if(selectedSite && currentProfile) {
            selectedSite.getSiteStats(currentProfile.accessToken).then(() => {
                setFormDataFromSiteStats().then(() => {
                    setDataFetched(true);
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSite, currentProfile]);

    const handleChange = (e) => {
        const formDataClone = createDeepClone(formData);
        const formDataErrorsClone = createDeepClone(formData);
        Object.assign(formDataClone, e);
        if(e) {
            let correspondingErrorKey = `${Object.keys(e)[0]}Error`;
            Object.assign(formDataErrorsClone, { [correspondingErrorKey]: '' });
            setFormDataErrors(formDataErrorsClone);
        }

        setFormData(formDataClone);
    };

    const handleImageUpload = async (e, type) => {
        let updatedFormData = createDeepClone(formData);
        if(type === "delete" && getObjectValue(updatedFormData, "map")) {
            updatedFormData["map"] = null;
        } else {
            let image = e[0];
            updatedFormData["map"] = { 'file': image, 'error': false, 'success': false, 'name': `${site}-map.${image.name.split(".")[1]}` };
        }
        setFormData(updatedFormData);
    };

    if(selectedSite && currentProfile && dataFetched) {
        let sitesURI = "/sites";
        let adminLinks = [];
        let writeLinks = [];
        if(currentProfile.isAdmin) {
            // sitesURI = `/${sub}/sites`;
            adminLinks = [{ "display": "Label Images", "url": `${sitesURI}/${site}/label`}, { "display": "Stats", "url": `${sitesURI}/${site}/stats`}];
        }
        if(selectedSite.access === "write" || selectedSite.access === "owner" || currentProfile.isAdmin) {
            writeLinks = [{ "display": "Image Upload", "url": `${sitesURI}/${site}/upload` }];
        }
        let sideNavbarFilters = [{ "display": "Buck Report", "url": `${sitesURI}/${site}/buck_report` }, { "display": "Buck Inventory", "url": `${sitesURI}/${site}/inventory` }, ...writeLinks, ...adminLinks];

        return (
            <>
                <SideNavbar isMobile={isMobile} propFilters={sideNavbarFilters} title={formattedSite} titleUrl={sitesURI} initialShow={true} />
                <div style={isMobile ? { "minHeight":"92vh" } : { "minHeight":"92vh", "maxWidth": "90%", "display": "flex", "justifyContent": "center" }} className="w3-content w3-container w3-padding-64" id="about">
                    <div style={isMobile ? { "width": "100%" } : { "width": "75%" }}>
                        <div style={{ "display": "flex", "alignItems": "center", "marginBottom": "16px" }}>
                            <h3 style={{ "width": "250px", "margin": "0px" }}>{formattedSite} Stats</h3>
                            <div style={{ "width": "100%", "display": "flex", "justifyContent": "left" }}>
                                <div>
                                    <HoverButton
                                        normalIcon={<RiFileEditLine style={{ "transform": "scale(2.5)", "color": "grey" }} className="ai-edit-icon" />}
                                        hoverIcon={<RiFileEditFill style={{ "transform": "scale(2.5)", "color": "black" }} className="ai-edit-icon" />}
                                        updateParent={() => { setEditStats(!editStats); }}
                                        title={`Edit Stats`}
                                        style={{ "alignItems": "top", "paddingRight": "16px", "width": "100%", "justifyContent": "right" }}
                                    />
                                </div>
                            </div>
                        </div>
                        {editStats ? (
                            <div style={isMobile ? { "width": "100%"} : { "width": "50%"} }>
                                <form 
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        setSubmitted(true);
                                        let postData = { ...formData };
                                        if(formData.map && existingImage && existingImage.size === formData.map.file.size && existingImage.lastModified === formData.map.file.lastModified) {
                                            Object.assign(postData, { 'map': existingImage.name })
                                        } else if(formData.map) {
                                            selectedSite.uploadImages(currentProfile.accessToken, { [getFilePseudoUUID(formData.map.file)]: formData.map }, false);
                                            Object.assign(postData, { 'map': formData.map.name });
                                        }
                                        let response = await selectedSite.updateSiteStats(currentProfile.accessToken, postData);
                                        if(response) {
                                            setUpdateSuccess('Successfully saved stats.');
                                            await selectedSite.getSiteStats(currentProfile.accessToken);
                                            setTimeout(async () => {
                                                setEditStats(false);
                                                setSubmitted(false);
                                                setUpdateSuccess('');
                                                await setFormDataFromSiteStats();
                                            }, 1000);
                                        } else {
                                            setUpdateError('Failed to save stats, please refresh the page and try again.');
                                        }
                                    }}
                                >
                                    {Object.keys(imageStatsFormObject).map((key) => {
                                        let formObject = imageStatsFormObject[key];
                                        let step = getObjectValue(formObject, "step") ? formObject.step : '';
                                        return(
                                            <div key={key} className="form-group">
                                                <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>{formObject.label}</label>
                                                <FormInput style={{ "border": "1px solid #ccc" }} disabled={submitted} isMobile={isMobile} updateParent={handleChange} name={key} type={formObject.type} object={formData} errors={formDataErrors} placeholder={formObject.label} step={step} />
                                            </div>
                                        );
                                    })}
                                    <DropZone acceptedFileTypes={{ 'image/*': ['.jpg', '.jpeg'] }} updateParent={handleImageUpload} submitted={submitted} display={true} files={formData.map ? [formData.map.file] : []} text={"Map image of the property with Trailcamera markings"} maxFiles={1} isMobile={isMobile} />
                                    <div style={{ "display": "flex", "justifyContent": "right" }}>
                                        <button style={{ "marginTop": "8px" }} className="main-button" type="submit" disabled={submitted}>Apply Changes</button>
                                    </div>
                                    <div style={{ "color": "red", "marginTop": "16px"}}>{updateError}</div>
                                    <div style={{ "color": "green", "marginTop": "16px" }}>{updateSuccess}</div>                                                                
                                </form>
                            </div>
                        ) : (
                            <div>
                                <p>Additional data manually input for the buck report generation</p>
                                {Object.keys(imageStatsFormObject).map((key) => {
                                    let formObject = imageStatsFormObject[key];
                                    return(
                                        <div key={key} className="form-group">
                                            <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>{formObject.label}</label>
                                            <p>{selectedSite[key]}</p>
                                        </div>
                                    );
                                })}
                                <div className="form-group">
                                    <label style={{ "width": "100%", "margin": "0px", "display": "block", "fontWeight": "bold" }}>Map Image</label>
                                    {selectedSite.map ? (
                                        <img style={{ "width": "250px", "marginTop": "15px" }} src={selectedSite.map} alt='Map that will be included in the report'/>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    } else if(!currentProfile && typeof currentProfile === "boolean") {
        return(
            <Error isMobile={isMobile} type={"login"} />
        );
    } else {
        return(
            <Loading isMobile={isMobile} text={`${formattedSite}`} />
        );
    }
}

SiteStats.propTypes = {
    dataView: PropTypes.bool,
    isMobile: PropTypes.bool,
    currentProfile: PropTypes.object
};

export default SiteStats;